import type {PosterOpts} from '@PosterWhiteboard/poster/poster.class';
import {Poster} from '@PosterWhiteboard/poster/poster.class';
import type {PosterLoadObject} from '@PosterWhiteboard/poster/poster.types';
import {MAX_POSTER_NAME_LENGTH, POSTER_VERSION} from '@PosterWhiteboard/poster/poster.types';
import {getPosterObjectFromPosterBackendObject} from '@PosterWhiteboard/poster/poster-backend';
import {getFitToScreenScale, modeHasBufferForFitToScreen} from '@PosterWhiteboard/poster/poster-scaling.class';
import {hideLoading, showLoading, updateLoadingProgress} from '@Libraries/loading-toast-library';
import {getUniqueString, incrementVersionInString} from '@Utils/string.util';
import {geCurrentUserData} from '@Libraries/user.library';
import type {PosterModeDetails} from '@PosterWhiteboard/poster/poster-mode.class';
import {PosterMode, PosterModeType} from '@PosterWhiteboard/poster/poster-mode.class';
import {consumeDeferredEditorUploads} from '@PosterWhiteboard/poster/consume-deferred-editor-uploads';
import {addAiChatImageToEditor} from '@PosterWhiteboard/poster/add-ai-chat-image-to-editor';
import {resizeToUpdatedDimensions} from '@PosterWhiteboard/libraries/resize-poster.library';
import {GA4EventName, GA4EventParamName, trackPosterBuilderGA4Events} from '@Libraries/ga-events';
import type {UserObject} from '@PosterWhiteboard/user/user.types';
import type {FolderObject} from '@PosterWhiteboard/classes/folder.class';
import type {PosterObjectBackend} from '@PosterWhiteboard/poster/poster-backend.types';
import {getPosterBackendObjectFromPoster} from '@PosterWhiteboard/poster/poster-frontend-to-backend';
import {posterLoadObjectWithNewUids} from '@Libraries/poster-object.library';
import {EVENT_WIZARD_SEMANTIC_TAGGING_LIMIT_ERROR_CODE} from '@PosterWhiteboard/poster/poster-backend.types';
import type {DeepPartial} from '@/global';
import {getPosterUi} from '@PosterWhiteboard/ui/poster-ui';
import {LoadingVariant} from '@Libraries/loading-toast.types';
import type {PartialDeep} from 'type-fest';
import {cloneDeep} from 'lodash';
import type {PageObject} from '@PosterWhiteboard/page/page.types';
import {GROWL_TYPE} from '@Libraries/message-growl.types';
import {transparencyStates} from '@PosterWhiteboard/items/image-item/image-item.class';
import type {ItemObject} from '@PosterWhiteboard/items/item/item.types';
import {isBackgroundItemObjectTransparent, isColorBackgroundObject, isImageBackgroundObject, isVideoBackgroundObject} from '@Libraries/page-object.library';
import {rgbaToRGBAString} from '@Utils/color.util';

interface NewPagesObject {
  pagesHashMap: Record<string, DeepPartial<PageObject> & {hashedID: string}>;
  pageOrder: string[];
}

// Pages beyond this many load their items/audio lazily after the editor becomes interactive.
const EAGER_PAGE_LOAD_COUNT = 3;

// Modes whose post-load flows tolerate lazily-loading pages. Generation must render every page
// up front, and RESIZE_TO/MORPH mutate all pages' live items right after load.
const PAGE_CONTENT_DEFER_MODE_TYPES: PosterModeType[] = [PosterModeType.EDIT, PosterModeType.COPY, PosterModeType.WEB_PAGE];

type PageObjectWithoutDeferredContent = Omit<PageObject, 'items' | 'audioClips'>;

interface PosterLoadObjectWithDeferredPageContent extends Omit<PosterLoadObject, 'pages'> {
  pages: {
    pagesHashMap: Record<string, PageObject | PageObjectWithoutDeferredContent>;
    pageOrder: string[];
  };
}

export interface PosterLoadOptions {
  onBackendObjectLaoded?(): void;
}

export interface LoadPosterConfig {
  posterHashId: string;
  posterVersionId?: string;
  isForGalleryTemplate?: boolean;
}

interface LoadPosterDataResponse {
  folder?: FolderObject;
  posterBackendObject: PosterObjectBackend;
  isFullPosterSaveRequired: boolean;
  isSignageRenderOutdated: boolean;
  signageOrderId: number | null;
  contributorFontsOnPoster: any[];
}
// eslint-disable-next-line @typescript-eslint/naming-convention
export const PosterMorphLoadingKey = 'PosterMorphLoadingKey';

export const loadPosterData = async (posterHashId: string, modeDetails?: PosterModeDetails, posterVersionId?: string): Promise<LoadPosterDataResponse> => {
  return (await window.PMW.readLocal('posterbuilder/loadPosterData', {
    hashedId: posterHashId,
    versionId: posterVersionId,
    posterVersionToLoad: POSTER_VERSION.CURRENT,
    jsVersion: window.PMW.VERSION,
    htmlVersion: window.PMW.HTML_VERSION,
    modeDetails,
  })) as LoadPosterDataResponse;
};

export const loadPosterTemplateFromHashId = async (posterHashId: string): Promise<void> => {
  if (!window.posterEditor?.whiteboard) {
    return;
  }
  const {posterBackendObject} = await loadPosterData(posterHashId, window.posterEditor.whiteboard.mode.details);
  const posterObject = getPosterObjectFromPosterBackendObject(posterBackendObject);
  const originalTemplatePageOrder = [...posterObject.pages.pageOrder];
  const posterObjectWithNewUids = posterLoadObjectWithNewUids(posterObject);

  await window.posterEditor.whiteboard.replacePosterWithTemplate(posterObjectWithNewUids, {
    originalTemplatePageOrder,
  });
};

export const loadPosterVersionFromHashedId = async (posterHashId: string, posterVersionId: string | undefined, undoable = false): Promise<void> => {
  if (!window.posterEditor?.whiteboard) {
    return;
  }
  const posterObject = await fetchPosterObjectForEditor(window.posterEditor.whiteboard, posterHashId, posterVersionId);

  await window.posterEditor.whiteboard.replacePosterWithVersion(posterObject, undoable);
};

/** Loads a poster into the open editor as the user's starting point: not undoable, and the undo stack restarts from it. */
export const loadPosterAsHistoryBaseline = async (posterHashId: string): Promise<void> => {
  if (!window.posterEditor?.whiteboard) {
    return;
  }
  const posterObject = await fetchPosterObjectForEditor(window.posterEditor.whiteboard, posterHashId);

  await window.posterEditor.whiteboard.replacePosterAsHistoryBaseline(posterObject);
};

const fetchPosterObjectForEditor = async (poster: Poster, posterHashId: string, posterVersionId?: string): Promise<PosterLoadObject> => {
  const {posterBackendObject} = await loadPosterData(posterHashId, poster.mode.details, posterVersionId);
  return getPosterObjectFromPosterBackendObject(posterBackendObject);
};

export const loadPosterDesignFromHashId = async (designHashId: string): Promise<void> => {
  if (!window.posterEditor?.whiteboard) {
    return;
  }
  const {posterBackendObject} = await loadPosterData(designHashId, window.posterEditor.whiteboard.mode.details);
  const posterObject = getPosterObjectFromPosterBackendObject(posterBackendObject);
  const designPosterId = posterObject.id;
  const originalDesignPageOrder = [...posterObject.pages.pageOrder];
  const posterObjectWithNewUids = posterLoadObjectWithNewUids(posterObject);

  await window.posterEditor.whiteboard.addDesignPagesToPoster(posterObjectWithNewUids, originalDesignPageOrder, designPosterId);
};

export const loadPosterDesignPageFromHashId = async (designHashId: string, pageHashedId: string): Promise<void> => {
  if (!window.posterEditor?.whiteboard) {
    return;
  }

  const {posterBackendObject} = await loadPosterData(designHashId, window.posterEditor.whiteboard.mode.details);
  const posterObject = getPosterObjectFromPosterBackendObject(posterBackendObject);
  const designPosterId = posterObject.id;
  const singlePageObject = filterPagesFromPosterObject(posterObject, [pageHashedId]);
  if (singlePageObject.pages.pageOrder.length === 0) {
    return;
  }

  const originalDesignPageOrder = [...singlePageObject.pages.pageOrder];
  const singlePageObjectWithNewUids = posterLoadObjectWithNewUids(singlePageObject);

  await window.posterEditor.whiteboard.addDesignPagesToPoster(singlePageObjectWithNewUids, originalDesignPageOrder, designPosterId);
};

export const loadPosterTemplatePageFromHashId = async (templateHashId: string, pageHashedId: string): Promise<void> => {
  if (!window.posterEditor?.whiteboard) {
    return;
  }

  const {posterBackendObject} = await loadPosterData(templateHashId, window.posterEditor.whiteboard.mode.details);
  const posterObject = getPosterObjectFromPosterBackendObject(posterBackendObject);
  const singlePageObject = filterPagesFromPosterObject(posterObject, [pageHashedId]);
  if (singlePageObject.pages.pageOrder.length === 0) {
    return;
  }

  const originalTemplatePageOrder = [...singlePageObject.pages.pageOrder];
  const singlePageObjectWithNewUids = posterLoadObjectWithNewUids(singlePageObject);

  await window.posterEditor.whiteboard.replacePosterWithTemplate(singlePageObjectWithNewUids, {
    originalTemplatePageOrder,
  });
};

export const replacePosterWithDesignFromHashId = async (designHashId: string): Promise<void> => {
  if (!window.posterEditor?.whiteboard) {
    return;
  }
  const {posterBackendObject} = await loadPosterData(designHashId, window.posterEditor.whiteboard.mode.details);
  const posterObject = getPosterObjectFromPosterBackendObject(posterBackendObject);
  const designPosterId = posterObject.id;
  const originalDesignPageOrder = [...posterObject.pages.pageOrder];
  const posterObjectWithNewUids = posterLoadObjectWithNewUids(posterObject);

  await window.posterEditor.whiteboard.replacePosterWithDesign(posterObjectWithNewUids, originalDesignPageOrder, designPosterId);
};

export const replacePosterWithDesignPageFromHashId = async (designHashId: string, pageHashedId: string): Promise<void> => {
  if (!window.posterEditor?.whiteboard) {
    return;
  }
  const {posterBackendObject} = await loadPosterData(designHashId, window.posterEditor.whiteboard.mode.details);
  const posterObject = getPosterObjectFromPosterBackendObject(posterBackendObject);
  const designPosterId = posterObject.id;
  const singlePageObject = filterPagesFromPosterObject(posterObject, [pageHashedId]);
  if (singlePageObject.pages.pageOrder.length === 0) {
    return;
  }
  const originalDesignPageOrder = [...singlePageObject.pages.pageOrder];
  const singlePageObjectWithNewUids = posterLoadObjectWithNewUids(singlePageObject);

  await window.posterEditor.whiteboard.replacePosterWithDesign(singlePageObjectWithNewUids, originalDesignPageOrder, designPosterId);
};

export const addPosterTemplateFromHashId = async (templateHashId: string): Promise<void> => {
  if (!window.posterEditor?.whiteboard) {
    return;
  }
  const {posterBackendObject} = await loadPosterData(templateHashId, window.posterEditor.whiteboard.mode.details);
  const posterObject = getPosterObjectFromPosterBackendObject(posterBackendObject);
  const originalTemplatePageOrder = [...posterObject.pages.pageOrder];
  const posterObjectWithNewUids = posterLoadObjectWithNewUids(posterObject);

  await window.posterEditor.whiteboard.addTemplatePagesToPoster(posterObjectWithNewUids, originalTemplatePageOrder);
};

export const addPosterTemplatePageFromHashId = async (templateHashId: string, pageHashedId: string): Promise<void> => {
  if (!window.posterEditor?.whiteboard) {
    return;
  }
  const {posterBackendObject} = await loadPosterData(templateHashId, window.posterEditor.whiteboard.mode.details);
  const posterObject = getPosterObjectFromPosterBackendObject(posterBackendObject);
  const singlePageObject = filterPagesFromPosterObject(posterObject, [pageHashedId]);
  if (singlePageObject.pages.pageOrder.length === 0) {
    return;
  }
  const originalTemplatePageOrder = [...singlePageObject.pages.pageOrder];
  const singlePageObjectWithNewUids = posterLoadObjectWithNewUids(singlePageObject);

  await window.posterEditor.whiteboard.addTemplatePagesToPoster(singlePageObjectWithNewUids, originalTemplatePageOrder);
};

// Morph loading brackets poster creation, so no Poster instance (and thus no poster.redux)
// exists yet when these run; the overlay-loader flag goes through the UI port instead.
export const terminatePosterMorphLoading = (key: string): void => {
  getPosterUi().setPosterOverlayLoaderVisibility(false);
  hideLoading(key);
};

const handleLoadPosterError = (error: Error): void => {
  if (error.message === EVENT_WIZARD_SEMANTIC_TAGGING_LIMIT_ERROR_CODE) {
    getPosterUi().showMessageGrowl({
      key: 'event-error',
      text: window.i18next.t('pmwjs_you_have_hit_the_limit'),
      type: GROWL_TYPE.DANGER,
      subText: window.i18next.t('pmwjs_daily_limit_error_desc'),
      showCloseIcon: true,
      interval: 100000000,
    });
    return;
  } else {
    getPosterUi().openErrorModal();
  }
};

export async function loadPoster(htmlElement: HTMLDivElement, {onBackendObjectLaoded = (): void => {}}: PosterLoadOptions, posterOpts: PosterOpts): Promise<Poster> {
  const {mode, ...posterOptsWithoutMode} = posterOpts;

  const posterMode = new PosterMode(mode.details);
  if (posterMode.details.type === PosterModeType.CREATE) {
    throw new Error(`Invalid poster mode type`);
  }

  if (mode.details.type === PosterModeType.MORPH) {
    initiatePosterMorphLoading(PosterMorphLoadingKey);
  }

  let posterBackendObject,
    folder,
    isFullPosterSaveRequired = true;
  let isSignageRenderOutdated: boolean;
  let signageOrderId: number | null;
  try {
    const response = await loadPosterData(posterMode.details.posterHashId, mode.details, posterMode.details.posterVersionId);
    posterBackendObject = response.posterBackendObject;
    folder = response.folder;
    isFullPosterSaveRequired = response.isFullPosterSaveRequired;
    isSignageRenderOutdated = response.isSignageRenderOutdated;
    signageOrderId = response.signageOrderId;
  } catch (e: unknown) {
    handleLoadPosterError(e as Error);
    throw e;
  } finally {
    if (mode.details.type === PosterModeType.MORPH) {
      terminatePosterMorphLoading(PosterMorphLoadingKey);
    }
  }
  onBackendObjectLaoded();

  const poster = new Poster(htmlElement, mode.details);
  poster.redux.updateSignageRenderState({isSignageRenderOutdated, signageOrderId});

  let posterObject = getPosterObjectFromPosterBackendObject(posterBackendObject);

  const savedPosterBackendObject = getPosterBackendObjectFromPoster(posterObject);

  if (posterMode.isCopyOfAPoster()) {
    posterObject = await getNewPosterObjectFromTemplate(posterObject);
    trackPosterBuilderGA4Events(GA4EventName.CREATE_POSTER, {[GA4EventParamName.TYPE]: posterMode.details.type});
  }

  if (posterMode.details.type === PosterModeType.GENERATE) {
    if (posterMode.details.optimizeItemLoad) {
      posterObject = removeAudioFromPageObjects(posterObject);
    }

    if ('pageHashedIdsToLoad' in mode.details && mode.details.pageHashedIdsToLoad) {
      posterObject = filterPagesFromPosterObject(posterObject, mode.details.pageHashedIdsToLoad);
    }

    if (posterMode.details.transparentBackground === true) {
      posterObject = forceTransparentOnColorBackgrounds(posterObject);
    } else if (posterMode.details.transparentBackground === false) {
      posterObject = forceWhiteOnTransparentBackgrounds(posterObject);
    }
  }

  if (!posterObject.width || !posterObject.height) {
    throw new Error(`Poster has invalid dimensions`);
  }
  try {
    // const posterObjectToLoad = mode.details.type === PosterModeType.MORPH ? await morphPoster(posterObject, mode.details.data) : posterObject;
    const posterObjectToLoad = posterObject;

    if (window.posterEditor) {
      window.posterEditor.whiteboard = poster;
    }

    if (posterMode.isWebpage()) {
      poster.redux.updateIsTimelineModeOn({isTimelineModeOn: true, pageIdToScrollToOnWhiteboardResize: undefined});
    }

    const posterObjectWithPageZoom = applyFitToScreenZoomToPages(posterObjectToLoad, posterMode);
    const pageIdsToDeferContentLoad = getPageIdsToDeferContentLoad(posterObjectWithPageZoom, posterMode);

    await poster.updateFromObject(
      {
        activePageId: posterObject.pages.pagesHashMap[posterObject.pages.pageOrder[0]].hashedID,
        folder,
        ...posterOptsWithoutMode,
        ...getPosterObjectWithDeferredPageContentStripped(posterObjectWithPageZoom, pageIdsToDeferContentLoad),
      },
      {
        updateRedux: false,
        undoable: false,
        onItemAddFail: onInitialItemLoadError,
      }
    );

    registerDeferredPageContent(poster, posterObjectWithPageZoom, pageIdsToDeferContentLoad);

    // In case user is making a copy of his own design, update the list of copied menu items
    if (Number(poster.creator?.id) === window.PMW.getUserId()) {
      updateCopiedMenuItems(poster);
    }
    poster.version = POSTER_VERSION.CURRENT;
    if (poster.mode.isCopy()) {
      await enableReplaceButtonForImageItems(poster);
      poster.savePoster.savedParentPosterBackendObject = poster.getBackendObject();
    }

    if (!isFullPosterSaveRequired) {
      poster.savePoster.setIsFullPosterSaveRequired(false);
      poster.savePoster.updateSavedPosterObject(savedPosterBackendObject);
    }
    resizePosterAfterLoad(poster.mode);
    poster.onPosterInit();
    startDeferredPageContentLoading(poster);
    if (posterMode.details.type === PosterModeType.EDIT) {
      void consumeDeferredEditorUploads(poster);
      if (posterMode.details.aiChatImageAssetHashedId) {
        void addAiChatImageToEditor(poster, posterMode.details.aiChatImageAssetHashedId);
      }
      const layersPendingJobId = posterMode.details.layersConfig?.pendingJobId;
      if (layersPendingJobId) {
        void getPosterUi().runPendingLayersFlow(layersPendingJobId, posterMode.details.layersConfig?.imageUrl);
      }
      const feedbackLayeringType = posterMode.details.layersConfig?.feedbackLayeringType;
      if (feedbackLayeringType) {
        getPosterUi().showLayeredPosterFeedback(posterMode.details.posterHashId, feedbackLayeringType);
      }
    }
  } catch (err) {
    console.error(err);
    // The renderer snapshots as soon as the poster reports ready; a partially loaded poster must fail the job, not ship.
    if (posterMode.isGeneration()) {
      throw err;
    }
  }

  return poster;
}

const applyFitToScreenZoomToPages = (posterObject: PosterLoadObject, posterMode: PosterMode): PosterLoadObject => {
  let scale = 1;
  if (posterMode.details.type === PosterModeType.GENERATE) {
    scale = posterMode.details.scale;
  } else {
    scale = getFitToScreenScale(posterObject.width, posterObject.height, modeHasBufferForFitToScreen(posterMode.details.type));
  }

  const modifiedPosterObject = {...posterObject};
  for (const [, page] of Object.entries(modifiedPosterObject.pages.pagesHashMap)) {
    page.zoom = scale;
  }
  return modifiedPosterObject;
};

const onInitialItemLoadError = (): void => {
  getPosterUi().showInitalPosterItemsLoadError();
};

const getPageIdsToDeferContentLoad = (posterObject: PosterLoadObject, posterMode: PosterMode): string[] => {
  if (!PAGE_CONTENT_DEFER_MODE_TYPES.includes(posterMode.details.type)) {
    return [];
  }
  return posterObject.pages.pageOrder.slice(EAGER_PAGE_LOAD_COUNT);
};

/**
 * Returns the poster object with items and audio stripped from the pages that will load lazily.
 * The pages themselves are still created (so page count, order and layout are correct immediately);
 * only their content is withheld from the initial load.
 */
const getPosterObjectWithDeferredPageContentStripped = (
  posterObject: PosterLoadObject,
  pageIdsToDeferContentLoad: string[]
): PosterLoadObject | PosterLoadObjectWithDeferredPageContent => {
  if (pageIdsToDeferContentLoad.length === 0) {
    return posterObject;
  }

  const pagesHashMap: Record<string, PageObject | PageObjectWithoutDeferredContent> = {};
  for (const [pageId, pageObject] of Object.entries(posterObject.pages.pagesHashMap)) {
    if (pageIdsToDeferContentLoad.includes(pageId)) {
      const {items: _items, audioClips: _audioClips, ...pageObjectWithoutContent} = pageObject;
      pagesHashMap[pageId] = pageObjectWithoutContent;
    } else {
      pagesHashMap[pageId] = pageObject;
    }
  }

  return {
    ...posterObject,
    pages: {
      pageOrder: posterObject.pages.pageOrder,
      pagesHashMap,
    },
  };
};

const registerDeferredPageContent = (poster: Poster, posterObject: PosterLoadObject, pageIdsToDeferContentLoad: string[]): void => {
  for (const pageId of pageIdsToDeferContentLoad) {
    const pageObject = posterObject.pages.pagesHashMap[pageId];
    const page = poster.pages.getPage(pageId);
    if (!pageObject || !page) {
      continue;
    }
    if (poster.mode.isCopy()) {
      enableReplaceButtonOnDeferredImageItemObjects(pageObject);
    }
    page.setDeferredPageContent(pageObject);
  }
};

// The deferred counterpart of enableReplaceButtonForImageItems: copy mode flags image items as
// replaceable, so pages that load lazily get the flag patched into their stored objects instead.
const enableReplaceButtonOnDeferredImageItemObjects = (pageObject: PageObject): void => {
  for (const itemObject of Object.values(pageObject.items.itemsHashMap)) {
    if ('showReplaceButton' in itemObject) {
      itemObject.showReplaceButton = true;
    }
  }
};

const startDeferredPageContentLoading = (poster: Poster): void => {
  void poster.pages.loadDeferredPagesContent().then(() => {
    if (Number(poster.creator?.id) === window.PMW.getUserId()) {
      updateCopiedMenuItems(poster);
    }
  });
};

type BackgroundItemTransform = (bg: ItemObject) => ItemObject;

// Backgrounds are now items (isBackground) inside page.items; map over each page's background items.
const mapPosterBackgrounds = (posterObject: PosterLoadObject, transform: BackgroundItemTransform): PosterLoadObject => {
  const modifiedPagesHashMap: Record<string, PageObject> = {};
  for (const [key, page] of Object.entries(posterObject.pages.pagesHashMap)) {
    const itemsHashMap: Record<string, ItemObject> = {};
    for (const [itemId, item] of Object.entries(page.items.itemsHashMap)) {
      itemsHashMap[itemId] = item.isBackground ? transform(item) : item;
    }
    modifiedPagesHashMap[key] = {
      ...page,
      items: {
        ...page.items,
        itemsHashMap,
      },
    };
  }
  return {
    ...posterObject,
    pages: {
      ...posterObject.pages,
      pagesHashMap: modifiedPagesHashMap,
    },
  };
};

const forceTransparentOnColorBackgrounds = (posterObject: PosterLoadObject): PosterLoadObject => {
  return mapPosterBackgrounds(posterObject, (bg) => {
    if (!isColorBackgroundObject(bg)) {
      return bg;
    }
    return {...bg, fill: null};
  });
};

const forceWhiteOnTransparentBackgrounds = (posterObject: PosterLoadObject): PosterLoadObject => {
  return mapPosterBackgrounds(posterObject, (bg) => {
    if (isColorBackgroundObject(bg) && isBackgroundItemObjectTransparent(bg)) {
      return {...bg, fill: rgbaToRGBAString([255, 255, 255, 1])};
    }
    if (isImageBackgroundObject(bg) && bg.transparency === transparencyStates.ON) {
      return {...bg, transparency: transparencyStates.OFF};
    }
    if (isVideoBackgroundObject(bg) && (bg.hasTransparency || bg.removeBackground.isBackgroundRemoved)) {
      return {
        ...bg,
        hasTransparency: false,
        removeBackground: {
          ...bg.removeBackground,
          isBackgroundRemoved: false,
        },
      };
    }
    return bg;
  });
};

const removeAudioFromPageObjects = (templateObject: PosterLoadObject): PosterLoadObject => {
  const modifiedPagesHashMap: Record<string, PageObject> = {};
  for (const [key, page] of Object.entries(templateObject.pages.pagesHashMap)) {
    modifiedPagesHashMap[key] = {
      ...page,
      audioClips: {
        ...page.audioClips,
        audioItemsHashMap: {},
      },
    };
  }
  return {
    ...templateObject,
    pages: {
      ...templateObject.pages,
      pagesHashMap: modifiedPagesHashMap,
    },
  };
};

export const filterPagesFromPosterObject = (posterObject: PosterLoadObject, pageHashedIdsToKeep: string[]): PosterLoadObject => {
  const {pageOrder, pagesHashMap} = posterObject.pages;

  const filteredPageOrder = pageOrder.filter((pageHashedId) => {
    return pageHashedIdsToKeep.includes(pageHashedId);
  });

  const filteredPagesMap: Record<string, PageObject> = {};
  for (const key of filteredPageOrder) {
    if (pagesHashMap[key]) {
      filteredPagesMap[key] = pagesHashMap[key];
    }
  }

  posterObject.pages.pageOrder = filteredPageOrder;
  posterObject.pages.pagesHashMap = filteredPagesMap;

  return posterObject;
};

export async function createPoster(htmlElement: HTMLDivElement, posterOpts: PosterOpts): Promise<Poster> {
  const {mode, posterBackendObject, ...posterOptsWithoutMode} = posterOpts;
  if (mode.details.type !== PosterModeType.CREATE) {
    throw new Error(`Invalid poster mode type`);
  }

  const poster = new Poster(htmlElement, mode.details);

  let posterObject: DeepPartial<PosterLoadObject> = {
    type: mode.details.posterType,
  };
  posterObject = await getNewPosterObjectFromTemplate(posterObject);

  try {
    if (window.posterEditor) {
      window.posterEditor.whiteboard = poster;
    }

    if (posterBackendObject) {
      const posterLoadObject = applyFitToScreenZoomToPages(getPosterObjectFromPosterBackendObject(posterBackendObject), poster.mode);
      await poster.updateFromObject(
        {
          ...posterLoadObject,
          activePageId: posterLoadObject.pages.pageOrder[0],
          ...posterOptsWithoutMode,
        },
        {updateRedux: false, undoable: false}
      );
    } else {
      const pages = getNewPagesObject(getFitToScreenScale(mode.details.posterWidth, mode.details.posterHeight, modeHasBufferForFitToScreen(mode.details.type)));
      await poster.updateFromObject(
        {
          activePageId: pages.pagesHashMap[pages.pageOrder[0]].hashedID,
          width: mode.details.posterWidth,
          height: mode.details.posterHeight,
          userWidth: mode.details.userWidth,
          userHeight: mode.details.userHeight,
          units: mode.details.units,
          pages,
          ...posterOptsWithoutMode,
          ...posterObject,
        },
        {updateRedux: false, undoable: false}
      );
    }
    poster.onPosterInit();
  } finally {
    hideLoading('loadingPoster');
  }
  return poster;
}

const updateCopiedMenuItems = (poster: Poster): void => {
  const items = poster.getAllItems();
  for (const item of items) {
    if (item.isMenu()) {
      item.updateCopiedItems(item.itemIds);
    }
  }
};

const getNewPagesObject = (zoom = 1): NewPagesObject => {
  const pageHashId = getUniqueString();
  return {
    pagesHashMap: {
      [pageHashId]: {
        hashedID: pageHashId,
        zoom,
      },
    },
    pageOrder: [pageHashId],
  };
};

const resizePosterAfterLoad = (mode: PosterMode): void => {
  if (mode.details.type === PosterModeType.RESIZE_TO) {
    resizeToUpdatedDimensions(mode.details);
  }
};

const enableReplaceButtonForImageItems = async (poster: Poster): Promise<void> => {
  const promises = [];
  const imageItems = poster.getAllImageItems();
  for (const imageItem of imageItems) {
    promises.push(
      imageItem.updateFromObject(
        {
          showReplaceButton: true,
        },
        {
          undoable: false,
          updateRedux: false,
        }
      )
    );
  }

  await Promise.all(promises);
};

const getNewPosterObjectFromTemplate = async <T extends DeepPartial<PosterLoadObject>>(templateObject: T): Promise<T> => {
  let isUserCreatorOfTemplate = false;
  const newPosterObject: T = {
    ...cloneDeep(templateObject),
    id: '',
    name: 'A New Design',
    description: '',
    isInternal: false,
    isTemplate: false,
    isPublic: false,
    isCopyable: true,
    isPurchaseable: true,
    createdOn: 0,
    lastModified: 0,
    idLastModifier: undefined,
  };

  const pagesHashMap = newPosterObject.pages?.pagesHashMap;
  const pagesOrder = newPosterObject.pages?.pageOrder;
  const newPagesOrder: string[] = [];
  const newPageHashmap: Record<string, PartialDeep<PageObject>> = {};
  if (pagesOrder && pagesHashMap) {
    for (let i = 0; i < pagesOrder?.length; i++) {
      const page = pagesHashMap[pagesOrder[i]];
      if (!page) {
        continue;
      }
      page.templateData = {
        pageId: page.hashedID,
        posterId: templateObject.id,
      };
      const newPageHashedID = getUniqueString();
      page.hashedID = newPageHashedID;
      newPagesOrder.push(newPageHashedID);
      newPageHashmap[newPageHashedID] = {...page};
    }
  }

  if (newPosterObject?.pages?.pageOrder) {
    newPosterObject.pages.pageOrder = newPagesOrder;
    newPosterObject.pages.pagesHashMap = {...newPageHashmap};
  }

  if (templateObject.creator) {
    newPosterObject.owner = templateObject.creator;

    if (Number(templateObject.creator.id) === window.PMW.getUserId()) {
      newPosterObject.description = templateObject.description;
      isUserCreatorOfTemplate = true;
    }

    if (isUserCreatorOfTemplate) {
      newPosterObject.name = incrementVersionInString(templateObject.name ?? '', MAX_POSTER_NAME_LENGTH);
    } else {
      newPosterObject.name = templateObject.name ?? '';
    }
  }

  clearAppliedPaletteFromAllPages(newPosterObject);

  newPosterObject.creator = await getCurrentUserObject();
  return newPosterObject;
};

const clearAppliedPaletteFromAllPages = (newPosterObject: DeepPartial<PosterLoadObject>): void => {
  const pages = newPosterObject.pages?.pagesHashMap;
  if (pages) {
    for (const page of Object.values(pages)) {
      if (page) {
        page.appliedPalette = undefined;
      }
    }
  }
};

const getCurrentUserObject = async (): Promise<UserObject | undefined> => {
  const userData = await geCurrentUserData();
  if (!userData) {
    return undefined;
  }

  return {
    id: userData.id,
    fbId: userData.fbId,
    name: userData.name,
    type: userData.type,
    watermark: userData.watermark !== '-1' ? userData.watermark : undefined,
    premiumLevel: userData.premiumLevel,
    preferredLanguage: userData.preferredLanguage,
    verificationNeededStatus: userData.verificationNeededStatus,
  };
};

export const initiatePosterMorphLoading = (key: string, loadingText = 'pmwjs_updating_with_ai'): void => {
  getPosterUi().setPosterOverlayLoaderVisibility(true);

  showLoading(key, {
    text: window.i18next.t(loadingText),
    priority: 10,
    progress: 0,
    variant: LoadingVariant.AI,
  });
  const progressUpdates = [
    {progress: 0.18, delay: 2000},
    {progress: 0.37, delay: 1000},
    {progress: 0.55, delay: 700},
    {progress: 0.88, delay: 800},
    {progress: 0.94, delay: 1000},
  ];

  let cumulativeDelay = 0;

  progressUpdates.forEach(({progress, delay}) => {
    cumulativeDelay += delay;
    setTimeout(() => {
      updateLoadingProgress(key, progress);
    }, cumulativeDelay);
  });
};

export const createPosterForQRCodeTemplate = async (posterHashedId: string): Promise<string> => {
  return (await window.PMW.writeLocal('poster/createPosterForQRCodeTemplate', {
    hashedId: posterHashedId,
  })) as string;
};
